import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import styled, { withTheme } from 'styled-components';
import { hook } from '@g4/automation-hooks';
import Link from '@g4/next-furniture/Link';
import tracking from '@g4/g4-analytics';

import useBookingSteps from '../hooks/useBookingSteps';
import { useBookingPath } from '../graphql/index.js';
import useOnClickOutside, {
  preventPropagationProps,
} from '../hooks/useOnClickOutside';
import useTranslationsQuery from '../graphql/useTranslationsQuery';

import { ConditionalWrap, Text } from './Boneyard';
import RadialProgress from './RadialProgress';

import BREADCRUMB_TRANSLATIONS_QUERY from './breadcrumb/breadcrumbTranslations.query.graphql.js';

import { FLIGHTS_BREADCRUMB } from '../../automationHooks.json';
import { useRouter } from 'next/router';
/**
 * TODO:
 * - ensure existing session is maintained (search params and transactionId)
 */

const propTypes = {
  compact: PropTypes.bool,
  theme: PropTypes.object.isRequired,
  backgroundColor: PropTypes.string,
};

const BreadcrumbNav = styled.nav.attrs({
  'aria-label': 'breadcrumb',
})`
  position: absolute;
  bottom: -15px;
  right: 0;
  transform: translateY(100%);
  z-index: ${props => props.theme.zIndex.breadcrumbNav.container};
  padding: ${props => props.theme.spacing(1)} 0;
  background: white;
  border-radius: 8px;
  box-shadow: 0 -5px 28px 0px rgba(100, 100, 99, 0.3);
  cursor: initial;
  ${props => props.theme.query.md} {
    padding: ${props => props.theme.spacing(4)} 0;
    position: relative;
    background: ${props =>
      props.backgroundColor ? props.backgroundColor : 'transparent'};
    box-shadow: none;
    transform: none;
    bottom: auto;
    right: auto;
  }

  &:after {
    display: block;
    content: '';
    width: 0;
    height: 0;
    border-style: solid;
    border-width: 0 15px 15px 15px;
    border-color: transparent transparent white transparent;
    position: absolute;
    top: -15px;
    right: 15px;
    z-index: ${props => props.theme.zIndex.breadcrumbNav.containerArrow};
    ${props => props.theme.query.md} {
      display: none;
    }
  }
`;

const BreadcrumbList = styled.ol`
  display: flex;
  flex-direction: column;
  max-width: 1200px;
  margin: 0 auto;
  ${props => props.theme.query.md} {
    flex-direction: row;
    justify-content: space-between;
  }
`;

const BreadcrumbItem = styled.li.attrs(props => ({
  ...(props.isActive ? { 'aria-current': 'page' } : {}),
}))`
  flex: 1;
  white-space: nowrap;
  font-size: 14px;
  letter-spacing: 0.2px;
  line-height: 36px;
  padding: 0 ${props => props.theme.spacing(10)} 0
    ${props => props.theme.spacing(5)};

  ${props => props.theme.query.md} {
    font-size: 18px;
    padding: 0 0 ${props => props.theme.spacing(1)} 0;
    letter-spacing: 0;
    line-height: 24px;
    margin: 0 ${props => props.theme.spacing(1)};
  }

  a {
    display: block;
  }

  ${props => {
    if (props.isLink) {
      return `
        color: ${props.theme.colors.breadcrumb.link.text};
        ${props.theme.query.md} {
        color: ${props.theme.colors.breadcrumb.link.text};
          border-bottom: 4px ${props.theme.colors.breadcrumb.link.border} solid;
        }

        &:hover span {
          font-weight: 600;
        }
      `;
    }
    if (props.isActive) {
      return `
        color: ${props.theme.colors.breadcrumb.active.text};
        background-color: ${props.theme.colors.breadcrumb.active.background}
        ${props.theme.query.md} {
          border-bottom: 4px ${props.theme.colors.breadcrumb.active.border} solid;
          background-color: transparent;
        }
      `;
    }
    return `
      color: ${props.theme.colors.breadcrumb.inactive.text};
      ${props.theme.query.md} {
        border-bottom: 4px ${props.theme.colors.breadcrumb.inactive.border} solid;
      }
    `;
  }}
`;

const ItemTitle = styled.span`
  font-weight: ${props => (props.isActive ? 600 : 'normal')};
  ${props => props.theme.query.md} {
    margin-left: ${props => props.theme.spacing(2)};
  }
`;

const ProgressNumber = styled.span`
  font-weight: 600;
`;

const ClickableRelativeWrapper = styled.div`
  flex-shrink: 0;
  position: relative;
  cursor: pointer;
`;

const Breadcrumb = ({ compact, theme, backgroundColor }) => {
  const translations = useTranslationsQuery(BREADCRUMB_TRANSLATIONS_QUERY);

  const { steps, activeIndex } = useBookingSteps();
  const { isStadium } = useBookingPath();
  const router = useRouter();

  const stepsIndexMap = useMemo(
    () =>
      steps.reduce((indexMap, step, index) => {
        return {
          ...indexMap,
          [step.key]: index,
        };
      }, {}),
    [steps]
  );

  const currentStep =
    isStadium &&
    stepsIndexMap['bundles'] &&
    activeIndex > stepsIndexMap['bundles']
      ? activeIndex
      : activeIndex + 1;

  const numberOfSteps = isStadium ? steps.length - 1 : steps.length;

  const [breadcrumbsAreVisible, setBreadcrumbsAreVisible] = React.useState(
    compact ? false : true
  );
  const toggleAreBreadcrumbsVisible = () =>
    setBreadcrumbsAreVisible(prevState => !prevState);

  const breadcrumbsContentRef = React.useRef();

  useOnClickOutside(
    breadcrumbsContentRef,
    () => compact && setBreadcrumbsAreVisible(false)
  );

  const getUpdatedQueryParams = href => {
    const { isStadium, transactionId, ...rest } = router.query;
    const prefix = href.includes('?') ? '&' : '?';
    return Object.keys(rest).length > 0
      ? `${prefix}${new URLSearchParams(rest).toString()}`
      : '';
  };

  return (
    <ConditionalWrap
      condition={compact}
      wrap={children => (
        <ClickableRelativeWrapper
          onClick={toggleAreBreadcrumbsVisible}
          {...hook(FLIGHTS_BREADCRUMB.hook, 'toggle')}
        >
          {children}
        </ClickableRelativeWrapper>
      )}
    >
      {compact && (
        <RadialProgress
          steps={numberOfSteps}
          step={currentStep}
          radius={30}
          strokeWidth={4}
          color={theme.colors.breadcrumb.compactView.activeSegment}
          backgroundColor={theme.colors.breadcrumb.compactView.inactiveSegment}
        >
          <Text color={theme.colors.breadcrumb.compactView.text} size={12}>
            <ProgressNumber>{currentStep}</ProgressNumber> of{' '}
            <ProgressNumber>{numberOfSteps}</ProgressNumber>
          </Text>
        </RadialProgress>
      )}
      {breadcrumbsAreVisible && (
        <BreadcrumbNav
          ref={breadcrumbsContentRef}
          {...preventPropagationProps()}
          backgroundColor={backgroundColor}
        >
          <BreadcrumbList>
            {steps.map(({ key, href, isActive, as }, index) => {
              const isLink = index < activeIndex;
              const stepNumber =
                isStadium &&
                stepsIndexMap['bundles'] &&
                index > stepsIndexMap['bundles']
                  ? index
                  : index + 1;

              // Get step title from translations query
              const stepTitle = translations
                ? translations.breadcrumb[`${key}StepTitle`]
                : '';

              if (isStadium && key === 'bundles') {
                return null;
              }

              return (
                <BreadcrumbItem
                  isLink={isLink}
                  isActive={isActive}
                  key={`breadcrumb-${key}`}
                >
                  <ConditionalWrap
                    condition={isLink}
                    wrap={children => (
                      <Link
                        href={`${href}${getUpdatedQueryParams(href)}`}
                        as={`${as}${getUpdatedQueryParams(as)}`}
                        clientRoute
                      >
                        {({ anchorProps }) => {
                          return (
                            <a
                              {...anchorProps}
                              onClick={() => {
                                tracking.trackEvent(
                                  'site_click_tracking',
                                  'Navigation',
                                  stepTitle
                                );
                              }}
                            >
                              {children}
                            </a>
                          );
                        }}
                      </Link>
                    )}
                  >
                    {stepNumber}
                    {compact && '. '}
                    <ItemTitle
                      isActive={isActive}
                      {...hook(
                        FLIGHTS_BREADCRUMB.hook,
                        `item-${key.toLowerCase()}`
                      )}
                    >
                      {stepTitle}
                    </ItemTitle>
                  </ConditionalWrap>
                </BreadcrumbItem>
              );
            })}
          </BreadcrumbList>
        </BreadcrumbNav>
      )}
    </ConditionalWrap>
  );
};

Breadcrumb.propTypes = propTypes;

export default withTheme(Breadcrumb);
